<template>
  <div class="fill-parent-container">
    <div class="centered">
      <div
        class="container-answer noselect"
        :class="[isBig ? 'long-answer' : 'short-answer']"
        :id="answerDomElementId"
      >
        <span
          v-for="(word, i) in splitWords"
          class="word"
          :key="i"
        >
          <span class="height-fix" v-if="splitWords.length > 1 && word.text === ' '"></span>
          <span
            v-for="(letter, j) in word.text"
            :class="getClass(word.text, word.startingIndex + j)"
            :key="letter + word.startingIndex + j"
            aria-hidden="true"
            v-html="letter"
          ></span>
        </span>
        <span class="blinker">_</span>
      </div>
      <done-button
        v-if="answerLocal && answerLocal.trim().length > 0"
        :class="['done-button', answerLocal ? '' : 'hidden']"
        :on-click="done"
        :transition="innerTransition"
        text='Done'
      >
      </done-button>
    </div>
  </div>
</template>

<script>
import uuidv4 from 'uuid/v4';
import { createNamespacedHelpers } from 'vuex';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import DoneButton from 'components/activity/DoneButton.vue';

const typingActivityNamespace = createNamespacedHelpers('activity/typing');
const LIMIT = 3000;
const BACKSPACE = 'backspace';
const ENTER = 'enter';
const TAB = 'tab';

export default {
  components: {
    DoneButton
  },
  props: {
    doneCallback: {
      type: Function,
      required: true
    },
    answer: {
      required: false
    },
    isBig: {
      type: Boolean,
      required: false
    }
  },
  mixins: [TypingActivityHelper],

  data() {
    return {
      answerLocal: '',
      answerDomElementId: uuidv4(),
      innerTransition: 'fade-10',
      wasDoneButtonPressed: false
    };
  },

  computed: {
    splitWords() {
      const words = this.answerLocal.split(' ').map(text => ({
        text: text.trim()
      }));

      let prevStartingIndex = null;
      words.forEach((w, i) => {
        const word = words[i];
        if (!prevStartingIndex) {
          prevStartingIndex = 0;
        }

        word.startingIndex = prevStartingIndex;
        // add space that was removed by split, except for last element
        word.text = i === words.length - 1 ? word.text : word.text.concat(' ');
        prevStartingIndex += word.text.length;
      });

      return words;
    }
  },

  created() {
    this.answerLocal = this.answer || ' ';
    this.setFreeTextTyping(true);
  },

  methods: {
    ...typingActivityNamespace.mapMutations(['setFreeTextTyping']),

    isCursor(letterIndex) {
      const lettersTyped = this.splitWords.reduce((acc, array) => acc + array.text.length, 0);
      const hasCursor = lettersTyped === 0 || lettersTyped === letterIndex + 1;
      return hasCursor;
    },

    getClass(word, letterIndex) {
      const lettersTyped = this.splitWords.reduce((acc, array) => acc + array.text.length, 0);
      // const hasCursor = lettersTyped === 0 || lettersTyped === letterIndex + 1;
      // const whiteSpace = hasCursor ? 'nowrap' : 'white-space';
      const emptyTyping = lettersTyped === 1 ? 'empty' : '';
      const emptyWords = word === ' ' ? 'white-space wrap-initial' : '';

      return [
        `char${letterIndex + 1}`,
        'white-space',
        emptyTyping,
        emptyWords
      ];
    },

    handleCorrectKey({ character }) {
      if (this.wasDoneButtonPressed) {
        return;
      }

      const lowerChr = character.toLowerCase();
      const tabOrEnter = lowerChr === TAB || lowerChr === ENTER;
      if (tabOrEnter || this.answerLocal.length === LIMIT) {
        // skip if ENTER or if maximum character limit has been reached
        return;
      }

      if (lowerChr === BACKSPACE) {
        // remove last character if backspace
        this.answerLocal = this.answerLocal.slice(0, -1) || ' ';
      } else {
        if (this.answerLocal === ' ') {
          this.answerLocal = '';
        }
        // append character
        this.answerLocal = `${this.answerLocal}${character}`;
      }

      this.incrementTypingIndex();
      this.$nextTick(() => {
        // this will be replaced in vue2 with $refs
        const answerDOM = document.getElementById(this.answerDomElementId);
        answerDOM.scrollTop = answerDOM.offsetHeight;
      });
    },

    done() {
      this.wasDoneButtonPressed = true;
      this.doneCallback(this.answerLocal);
    }
  }
};
</script>

<style lang="scss" scoped>
.checkmark-icon {
  vertical-align: top;
  display: inline-block;
  margin-left: 15px;
}

.fill-parent-container {
  max-width: 100%;
  max-height: 180px;
  overflow: hidden;
  margin: 0 auto;
}

.container-answer {
  width: calc(100% - 80px);
  display: inline-block;
  border: 2px solid #000;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 30px;
  box-sizing: border-box;
}

 .long-answer {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 130px;
 }

 .short-answer {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 130px;
 }

.container-done {
  display: flex;
  flex-direction: column;
  flex-basis: 60px;
  flex-shrink: 0;
  margin: auto;
  color: green;
}

.paragraph {
  font-size: 24px;
  padding: 10px;
}

.cursor {
  min-height: 35px;
  position: relative;
}

.blinker {
  vertical-align: middle;
  animation: blinky 0.65s step-end infinite;
  animation-iteration-count: infinite;
  opacity: 1;
}

.word {
  display: inline-block;
  vertical-align: middle;
}

.white-space {
  white-space: pre-wrap;
  vertical-align: middle;
}

.nowrap {
  white-space: nowrap;
  vertical-align: middle;
}

.pre {
  white-space: normal;
  vertical-align: middle;
}

.wrap-initial {
  white-space: initial !important;
  vertical-align: middle;
}

@keyframes blinky {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0
  }

  100% {
    opacity: 1;
  }
}

.centered {
  margin: 0 auto 0 auto;
  width: 100%;
}
.height-fix {
  display: inline-block;
  height: 20px;
  width: 5px;
}

.cursor.error::after {
  border-bottom: 2px solid #f00;
}
</style>
